import { useState, useRef, useCallback } from "react"
import { ChatAnalyze } from "../interfaces"

interface UseChatAnalyzeReturn {
  analyze: (file: File) => void
  result: ChatAnalyze.Stats | null
  isLoading: boolean
  progress: number
  progressMsg: string
  error: string | null
  reset: () => void
}

export function useChatAnalyze(): UseChatAnalyzeReturn {
  const [result, setResult] = useState<ChatAnalyze.Stats | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [progressMsg, setProgressMsg] = useState("")
  const [error, setError] = useState<string | null>(null)

  const workerRef = useRef<Worker | null>(null)

  const reset = useCallback(() => {
    workerRef.current?.terminate()
    workerRef.current = null
    setResult(null)
    setIsLoading(false)
    setProgress(0)
    setProgressMsg("")
    setError(null)
  }, [])

  const analyze = useCallback((file: File) => {
    // Завершаем предыдущий воркер, если он был
    workerRef.current?.terminate()

    setResult(null)
    setError(null)
    setProgress(0)
    setProgressMsg("Чтение файла...")
    setIsLoading(true)

    const worker = new Worker(
      new URL("../workers/analyzeWorker.ts", import.meta.url),
      { type: "module" },
    )
    workerRef.current = worker

    worker.onmessage = (ev: MessageEvent<ChatAnalyze.WorkerOutMessage>) => {
      const msg = ev.data
      switch (msg.type) {
        case "PROGRESS":
          setProgress(msg.percent)
          setProgressMsg(msg.message)
          break
        case "DONE":
          setResult(msg.result)
          setProgress(100)
          setProgressMsg("Готово!")
          setIsLoading(false)
          worker.terminate()
          workerRef.current = null
          break
        case "ERROR":
          setError(msg.message)
          setIsLoading(false)
          worker.terminate()
          workerRef.current = null
          break
      }
    }

    worker.onerror = (ev) => {
      setError(ev.message ?? "Ошибка в Web Worker")
      setIsLoading(false)
      worker.terminate()
      workerRef.current = null
    }

    worker.postMessage({
      type: "ANALYZE",
      file,
    } satisfies ChatAnalyze.WorkerInMessage)
  }, [])

  return {
    analyze,
    result,
    isLoading,
    progress,
    progressMsg,
    error,
    reset,
  }
}

// ── Multi-file types ──────────────────────────────────────────────────────────

export type FileAnalysisStatus = "pending" | "analyzing" | "done" | "error"

export interface FileAnalysisEntry {
  file: File
  status: FileAnalysisStatus
  progress: number
  progressMsg: string
  result: ChatAnalyze.Stats | null
  fileHash: string
  error: string | null
}

interface UseMultiFileAnalyzeReturn {
  entries: FileAnalysisEntry[]
  addFiles: (files: File[]) => void
  reset: () => void
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useMultiFileAnalyze(): UseMultiFileAnalyzeReturn {
  const [entries, setEntries] = useState<FileAnalysisEntry[]>([])
  const workersRef = useRef<Map<number, Worker>>(new Map())
  // nextIdRef tracks the absolute insertion index; incremented synchronously
  // before async work begins so concurrent addFiles() calls get non-overlapping ranges
  const nextIdRef = useRef(0)

  const updateEntry = useCallback(
    (idx: number, patch: Partial<FileAnalysisEntry>) => {
      setEntries((prev) =>
        prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)),
      )
    },
    [],
  )

  const analyzeOne = useCallback(
    async (file: File, idx: number) => {
      updateEntry(idx, {
        status: "analyzing",
        progress: 0,
        progressMsg: "Чтение файла...",
      })

      const worker = new Worker(
        new URL("../workers/analyzeWorker.ts", import.meta.url),
        { type: "module" },
      )
      workersRef.current.set(idx, worker)

      worker.onmessage = (ev: MessageEvent<ChatAnalyze.WorkerOutMessage>) => {
        const msg = ev.data
        switch (msg.type) {
          case "PROGRESS":
            updateEntry(idx, {
              progress: msg.percent,
              progressMsg: msg.message,
            })
            break
          case "DONE":
            updateEntry(idx, {
              status: "done",
              progress: 100,
              progressMsg: "Готово",
              result: msg.result,
              fileHash: msg.fileHash,
            })
            worker.terminate()
            workersRef.current.delete(idx)
            break
          case "ERROR":
            updateEntry(idx, { status: "error", error: msg.message })
            worker.terminate()
            workersRef.current.delete(idx)
            break
        }
      }

      worker.onerror = (ev) => {
        updateEntry(idx, {
          status: "error",
          error: ev.message ?? "Ошибка в Web Worker",
        })
        worker.terminate()
        workersRef.current.delete(idx)
      }

      worker.postMessage({
        type: "ANALYZE",
        file,
      } satisfies ChatAnalyze.WorkerInMessage)
    },
    [updateEntry],
  )

  const addFiles = useCallback(
    (files: File[]) => {
      // Capture and advance the counter synchronously to avoid index collisions
      const startIdx = nextIdRef.current
      nextIdRef.current += files.length

      const newEntries: FileAnalysisEntry[] = files.map((file) => ({
        file,
        status: "pending" as FileAnalysisStatus,
        progress: 0,
        progressMsg: "",
        result: null,
        fileHash: "",
        error: null,
      }))

      setEntries((prev) => [...prev, ...newEntries])

      files.forEach((file, i) => {
        const idx = startIdx + i
        analyzeOne(file, idx).catch((err) => {
          updateEntry(idx, {
            status: "error",
            error: err instanceof Error ? err.message : String(err),
          })
        })
      })
    },
    [analyzeOne, updateEntry],
  )

  const reset = useCallback(() => {
    workersRef.current.forEach((w) => w.terminate())
    workersRef.current.clear()
    setEntries([])
    nextIdRef.current = 0
  }, [])

  return { entries, addFiles, reset }
}
